/**
 * Console sign-in (design: Admin Console › LOGIN). Microsoft / Google SSO, plus the email + password
 * fallback (D-02) with a TOTP step when MFA is enabled.
 */
import type { AuthProviders, LoginResponse } from '@uatp/shared';
import { Banner, Button, Input } from '@uatp/ui';
import logo from '@uatp/ui/logo.png';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { type ApiError, get, post } from '../api/client';
import { queryClient } from '../lib/query';
import { ME_KEY } from './auth';

function MicrosoftMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4H24v8.1h12.9c-.3 2.2-1.7 5.5-4.9 7.7l7.6 5.9c4.5-4.2 6.9-10.3 6.9-17.7z" />
      <path fill="#FBBC05" d="M10.5 28.6A14.6 14.6 0 0 1 9.7 24c0-1.6.3-3.2.8-4.7l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.2z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2 1.4-4.8 2.4-8.3 2.4-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.2C6.5 42.6 14.6 48 24 48z" />
    </svg>
  );
}

/** Only same-app paths are accepted as a post-login destination (no open redirects). */
function safePath(p: string | null): string {
  return p && p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/login') ? p : '/';
}

interface DemoInfo {
  admins: { email: string; role: string }[];
}

type Mode = 'sso' | 'password' | 'mfa';

export function LoginPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const returnTo = safePath(params.get('returnTo'));
  const [mode, setMode] = useState<Mode>('sso');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(params.get('message'));
  const [busy, setBusy] = useState(false);
  const firstField = useRef<HTMLInputElement>(null);

  const providers = useQuery({ queryKey: ['auth', 'providers'], queryFn: () => get<AuthProviders>('/auth/providers'), staleTime: Infinity });
  const demo = useQuery({
    queryKey: ['dev', 'demo'],
    queryFn: async () => {
      const res = await fetch('/api/v1/dev/demo');
      return res.ok ? ((await res.json()) as DemoInfo) : null;
    },
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    document.title = 'Sign in · UAT Proofing';
  }, []);

  useEffect(() => {
    if (mode !== 'sso') firstField.current?.focus();
  }, [mode]);

  // When neither SSO provider is configured, go straight to the email form.
  useEffect(() => {
    if (providers.data && !providers.data.microsoft && !providers.data.google) setMode('password');
  }, [providers.data]);

  const clearUrlError = () => {
    if (params.has('error')) {
      params.delete('error');
      params.delete('message');
      setParams(params, { replace: true });
    }
  };

  const sso = (provider: 'microsoft' | 'google') => {
    const target = `${window.location.origin}${returnTo}`;
    window.location.assign(`/api/v1/auth/sso/${provider}/start?returnTo=${encodeURIComponent(target)}`);
  };

  const finish = async () => {
    await queryClient.invalidateQueries({ queryKey: ME_KEY });
    navigate(returnTo, { replace: true });
  };

  const submitPassword = async (e: FormEvent) => {
    e.preventDefault();
    clearUrlError();
    setError(null);
    setBusy(true);
    try {
      const res = await post<LoginResponse>('/auth/login', { email, password });
      if (res.status === 'mfa_required') {
        setMode('mfa');
        setPassword('');
      } else await finish();
    } catch (err) {
      const e2 = err as ApiError;
      setError(e2.fieldErrors.email ? 'Enter a valid email address.' : e2.message);
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await post('/auth/mfa', { code });
      await finish();
    } catch (err) {
      setError((err as ApiError).message);
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  const p = providers.data;

  return (
    <main className="grid min-h-dvh place-items-center overflow-auto bg-bg p-8" data-screen-label="Admin sign in">
      <div className="flex w-full max-w-[400px] flex-col gap-4">
        <div className="flex items-center justify-center gap-2.5">
          <img src={logo} alt="" className="size-7 object-contain" />
          <span className="text-15 font-semibold">UAT Proofing System</span>
        </div>
        <div className="flex flex-col gap-5 rounded-lg border border-border bg-surface p-8 shadow-md">
          <div>
            <h1 className="text-h1">{mode === 'mfa' ? 'Two-step verification' : 'Sign in to the console'}</h1>
            <p className="mt-1.5 text-13 text-text-2">
              {mode === 'mfa'
                ? 'Enter the 6-digit code from your authenticator app.'
                : 'Use your organisation account. Access is granted by your global admin.'}
            </p>
          </div>

          {error && (
            <Banner variant="error" role="alert">
              {error}
            </Banner>
          )}

          {mode === 'sso' && (
            <div className="flex flex-col gap-2">
              {(p?.microsoft ?? true) && (
                <Button variant="secondary" size="lg" className="font-medium" onClick={() => sso('microsoft')} icon={<MicrosoftMark />} fullWidth>
                  Continue with Microsoft
                </Button>
              )}
              {(p?.google ?? true) && (
                <Button variant="secondary" size="lg" className="font-medium" onClick={() => sso('google')} icon={<GoogleMark />} fullWidth>
                  Continue with Google
                </Button>
              )}
              {(p?.password ?? true) && (
                <button
                  type="button"
                  className="mt-1 self-center text-13 font-medium text-primary-text hover:underline"
                  onClick={() => {
                    setError(null);
                    setMode('password');
                  }}
                >
                  Sign in with email instead
                </button>
              )}
            </div>
          )}

          {mode === 'password' && (
            <form className="flex flex-col gap-3" onSubmit={submitPassword} noValidate>
              <Input
                ref={firstField}
                label="Work email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Input
                label="Password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <Button type="submit" size="lg" loading={busy} loadingText="Signing in…" disabled={!email || !password} fullWidth>
                Sign in
              </Button>
              {(p?.microsoft || p?.google) && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 self-center text-13 font-medium text-text-2 hover:text-text"
                  onClick={() => {
                    setError(null);
                    setMode('sso');
                  }}
                >
                  <ArrowLeft className="size-3.5" aria-hidden />
                  Other sign-in options
                </button>
              )}
            </form>
          )}

          {mode === 'mfa' && (
            <form className="flex flex-col gap-3" onSubmit={submitMfa} noValidate>
              <Input
                ref={firstField}
                label="Verification code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                mono
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
              <Button type="submit" size="lg" loading={busy} loadingText="Verifying…" disabled={code.length !== 6} fullWidth>
                Verify
              </Button>
              <button
                type="button"
                className="self-center text-13 font-medium text-text-2 hover:text-text"
                onClick={() => {
                  setCode('');
                  setError(null);
                  setMode('password');
                }}
              >
                Start over
              </button>
            </form>
          )}

          {demo.data && mode !== 'mfa' && (
            <div className="rounded-md bg-surface-2 p-3 text-12 leading-[17px] text-text-2">
              <b className="text-text">Development:</b> demo admins{' '}
              {demo.data.admins.map((a, i) => (
                <span key={a.email}>
                  {i > 0 && ' · '}
                  <button
                    type="button"
                    className="font-semibold text-primary-text hover:underline"
                    onClick={() => {
                      setEmail(a.email);
                      setMode('password');
                    }}
                  >
                    {a.email}
                  </button>
                </span>
              ))}{' '}
              (password: the <code className="font-mono">DEMO_PASSWORD</code> setting).
            </div>
          )}

          <p className="text-center text-12 text-text-3">No access? Ask your global admin to add you in Users &amp; access.</p>
        </div>
      </div>
    </main>
  );
}
